// src/pages/SalarySmsSheet.jsx — responsive cards + table hybrid, actions (Copy SMS, CSV, Print)
// Enhanced: Branch/Staff header, Section 1: Applied Options, Section 2: Earnings (gross),
// Deductions with totals, and Advance Salary History (categorized)
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Tooltip,
  Typography,
  Button,
  IconButton,
  Snackbar,
  Skeleton,
  useMediaQuery,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime'; // Half day
import MoneyOffIcon from '@mui/icons-material/MoneyOff'; // No tips
import DoNotDisturbAltIcon from '@mui/icons-material/DoNotDisturbAlt'; // No service
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PersonIcon from '@mui/icons-material/Person';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const fmt = (n) => Number(n || 0).toFixed(2);
const money = (n) => `Rs ${fmt(n)}`;
const approxEq = (a, b, eps = 0.01) => Math.abs(Number(a) - Number(b)) <= eps;

export default function SalarySmsSheet() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [search] = useSearchParams();
  const staffId = search.get('staff_id');
  const year = search.get('year');
  const month = search.get('month');

  const [slip, setSlip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  const reload = useCallback(() => {
    if (!staffId || !year || !month) {
      setError('Missing query parameters.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    fetch(`https://ab.pizzahub.lk/api/salary_management.php?year=${year}&month=${month}`)
      .then((r) => r.json())
      .then((data) => {
        const found = Array.isArray(data) ? data.find((s) => String(s.staff_id) === String(staffId)) : null;
        if (!found) setError('No record found.');
        else setSlip(found);
      })
      .catch(() => setError('Failed to load data.'))
      .finally(() => setLoading(false));
  }, [staffId, year, month]);

  useEffect(() => { reload(); }, [reload]);

  const rows = useMemo(
    () => (slip && Array.isArray(slip.daily_breakdown) ? slip.daily_breakdown : []),
    [slip]
  );

  /* -------- Applied-options summary (counts + tips/service totals) -------- */
  const applied = useMemo(() => {
    if (!slip) return null;
    const perDay = Number(slip.basic_salary_per_day || 0);

    let halfDays = 0;
    let noTipDays = 0;
    let noSvcDays = 0;
    let tipTotal = 0;
    let svcTotal = 0;

    rows.forEach((d) => {
      const flagHalf =
        typeof d.flag_half !== 'undefined' ? !!d.flag_half : perDay > 0 && approxEq(d.daily_basic, perDay / 2);
      const flagNoTips = typeof d.flag_no_tips !== 'undefined' ? !!d.flag_no_tips : Number(d.daily_tip) === 0;
      const flagNoSvc = typeof d.flag_no_svc !== 'undefined' ? !!d.flag_no_svc : Number(d.daily_svc) === 0;
      if (flagHalf) halfDays++;
      if (flagNoTips) noTipDays++;
      if (flagNoSvc) noSvcDays++;
      tipTotal += Number(d.daily_tip) || 0;
      svcTotal += Number(d.daily_svc) || 0;
    });

    return { halfDays, noTipDays, noSvcDays, tipTotal, svcTotal, perDay };
  }, [slip, rows]);

  /* -------- Earnings & Deductions math -------- */
  const earnings = useMemo(() => {
    if (!slip) return null;
    const basic = Number(slip.basic_earning || 0);
    const bonus = Number(slip.bonus_total || 0);
    const tips = Number(applied?.tipTotal || 0);
    const svc = Number(applied?.svcTotal || 0);
    const daysPresent = Number(slip.days_present || 0);
    const gross = basic + bonus + tips + svc;
    return { daysPresent, basic, bonus, tips, svc, gross };
  }, [slip, applied]);

  const deductions = useMemo(() => {
    if (!slip) return null;
    const loan = Number(slip.loan_deduction || 0);
    const penalty = Number(slip.penalty_total || 0);
    const advance = Number(slip.advance_deduction || 0);
    const total = loan + penalty + advance;
    return { loan, penalty, advance, total };
  }, [slip]);

  // Show a mismatch indicator if API net != computed
  const calcNet = useMemo(() => {
    if (!earnings || !deductions) return null;
    return earnings.gross - deductions.total;
  }, [earnings, deductions]);

  const apiNet = Number(slip?.total_salary || 0);
  const netMismatch = calcNet != null && !approxEq(calcNet, apiNet);

  const mStr = String(month || '').padStart(2, '0');

  /* -------- Advance history (categorized) -------- */
  const advanceHistory = useMemo(() => {
    // try several likely property names
    const list =
      (slip && Array.isArray(slip.advance_history) && slip.advance_history) ||
      (slip && Array.isArray(slip.advances) && slip.advances) ||
      [];
    // Normalize each item
    const norm = list.map((x) => ({
      date: x.date || x.txn_date || x.created_at || '',
      amount: Number(x.amount || x.value || 0),
      category: x.category || x.type || 'Advance',
      note: x.note || x.remark || '',
    }));
    // Group by category
    const groups = norm.reduce((acc, it) => {
      (acc[it.category] = acc[it.category] || []).push(it);
      return acc;
    }, {});
    // Build totals per category
    const catTotals = Object.fromEntries(
      Object.entries(groups).map(([k, arr]) => [k, arr.reduce((s, i) => s + (i.amount || 0), 0)])
    );
    const grand = norm.reduce((s, i) => s + (i.amount || 0), 0);
    return { groups, catTotals, grand, flat: norm };
  }, [slip]);

  const handleCopySMS = () => {
    if (!slip) return;
    const lines = [
      `Salary Sheet — ${slip.staff_name} (${mStr}/${year}) [${slip.branch_name || slip.branch || 'Arugam Bay 1'}]`,
      `Days: ${earnings?.daysPresent ?? 0}`,
      `Basic: ${money(earnings?.basic)}`,
      `Tips: ${money(earnings?.tips)}`,
      `Service: ${money(earnings?.svc)}`,
      `Bonus: ${money(earnings?.bonus)}`,
      `Gross: ${money(earnings?.gross)}`,
      `Penalties: ${money(deductions?.penalty)}`,
      `Loan: ${money(deductions?.loan)}`,
      `Advance: ${money(deductions?.advance)}`,
      `Deductions: ${money(deductions?.total)}`,
      `Net Pay: ${money(apiNet)}${netMismatch ? ` (calc ${money(calcNet)})` : ''}`,
    ];
    const text = lines.join('\n');
    navigator.clipboard
      .writeText(text)
      .then(() => setSnack({ open: true, msg: 'Summary copied to clipboard', severity: 'success' }))
      .catch(() => setSnack({ open: true, msg: 'Copy failed', severity: 'error' }));
  };

  const handleCSV = () => {
    if (!rows.length) return setSnack({ open: true, msg: 'No daily data to export', severity: 'info' });
    const headers = ['date', 'daily_basic', 'daily_tip', 'daily_svc', 'daily_total', 'half_day', 'no_tips', 'no_service'];
    const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csv = [headers.join(',')]
      .concat(
        rows.map((d) =>
          [
            d.date,
            fmt(d.daily_basic),
            fmt(d.daily_tip),
            fmt(d.daily_svc),
            fmt(d.daily_total),
            d.flag_half ? 1 : 0,
            d.flag_no_tips ? 1 : 0,
            d.flag_no_svc ? 1 : 0,
          ]
            .map(escape)
            .join(',')
        )
      )
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `salary_${slip.staff_name || staffId}_${year}-${mStr}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Box maxWidth="1100px" mx="auto" p={2}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
            <Skeleton variant="text" width={280} height={32} />
            <Skeleton variant="rectangular" width={260} height={36} />
          </Stack>
        </Paper>
        <Skeleton variant="rectangular" height={160} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={320} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box maxWidth="800px" mx="auto" p={2} textAlign="center">
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={reload}>Retry</Button>
      </Box>
    );
  }

  return (
    <Box maxWidth="1100px" mx="auto" p={{ xs: 1.5, sm: 2 }}>
      {/* Sticky header / actions */}
      <Paper
        elevation={0}
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 1,
          p: { xs: 1.25, sm: 1.75 },
          mb: 2,
          borderRadius: 2,
          backgroundColor: theme.palette.mode === 'light' ? '#ffffffee' : '#0b0f14ee',
          border: `1px solid ${theme.palette.divider}`,
          backdropFilter: 'blur(6px)',
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" gap={1.25}>
          <Stack direction="row" gap={1.25} alignItems="center" flexWrap="wrap">
            <Chip icon={<PersonIcon />} label={slip.staff_name} variant="outlined" />
            {(slip.branch_name || slip.branch) && (
              <Chip icon={<StorefrontIcon />} label={slip.branch_name || slip.branch} variant="outlined" />
            )}
            <Chip icon={<CalendarMonthIcon />} label={`${String(month).padStart(2, '0')}/${year}`} />
            {'designation' in (slip || {}) && slip.designation && (
              <Chip variant="outlined" label={String(slip.designation)} />
            )}
            {'staff_code' in (slip || {}) && slip.staff_code && (
              <Chip variant="outlined" label={`ID: ${slip.staff_code}`} />
            )}
            <Chip color="success" variant="outlined" label={`Net: ${money(apiNet)}`} />
            {netMismatch && (
              <Chip color="warning" variant="outlined" label={`Calc Net: ${money(calcNet)}`} />
            )}
          </Stack>

          <Stack direction="row" gap={1}>
            <Tooltip title="Copy SMS summary">
              <span><IconButton onClick={handleCopySMS}><ContentCopyIcon /></IconButton></span>
            </Tooltip>
            <Tooltip title="Export daily CSV">
              <span><IconButton onClick={handleCSV}><FileDownloadIcon /></IconButton></span>
            </Tooltip>
            <Tooltip title="Print">
              <span><IconButton onClick={() => window.print()}><PrintIcon /></IconButton></span>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>

      {/* Title */}
      <Box textAlign="center" sx={{ mb: 1 }}>
        <Typography variant="h4" fontWeight={800}>Salary Sheet</Typography>
        <Typography variant="subtitle1" color="text.secondary">
          {slip.staff_name} — {String(month).padStart(2, '0')}/{year}
          {` — ${slip.branch_name || slip.branch || 'Arugam Bay 1'}`}
        </Typography>
      </Box>

      {/* SECTION 1 — Applied Options */}
      {applied && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Typography variant="subtitle1" gutterBottom>Applied Options</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
            <Chip size="small" icon={<AccessTimeIcon />} label={`Half Days: ${applied.halfDays}`} />
            <Chip size="small" icon={<MoneyOffIcon />} label={`No Tips: ${applied.noTipDays}`} color="warning" />
            <Chip size="small" icon={<DoNotDisturbAltIcon />} label={`No Service: ${applied.noSvcDays}`} color="error" />
            <Tooltip title="Zero values may also mean the pool was zero for that day.">
              <InfoOutlinedIcon fontSize="small" sx={{ opacity: .7, ml: 0.5 }} />
            </Tooltip>
          </Stack>
          <Divider sx={{ my: 1 }} />
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Chip variant="outlined" label={`Tips Total: ${money(applied.tipTotal)}`} />
            <Chip variant="outlined" label={`Service Total: ${money(applied.svcTotal)}`} />
          </Stack>
        </Paper>
      )}

      {/* SECTION 2 — Earnings + Deductions */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
          {/* Earnings */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Earnings</Typography>
            <Stack spacing={0.75}>
              <Row label="Days Attended" value={earnings?.daysPresent ?? 0} />
              <Row label="Basic Earned" value={money(earnings?.basic)} />
              <Row label="Tips Share" value={money(earnings?.tips)} />
              <Row label="Service-Charge Share" value={money(earnings?.svc)} />
              <Row label={<span style={{ color: 'green' }}>Bonuses</span>} value={<span style={{ color: 'green' }}>+{money(earnings?.bonus)}</span>} />
              <Divider sx={{ my: 0.5 }} />
              <Row bold label={<strong>Gross Total</strong>} value={<strong>{money(earnings?.gross)}</strong>} />
            </Stack>
          </Box>

          <Divider flexItem orientation={isMobile ? 'horizontal' : 'vertical'} />

          {/* Deductions */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Deductions</Typography>
            <Stack spacing={0.75}>
              <Row label={<span style={{ color: 'red' }}>Penalties</span>} value={<span style={{ color: 'red' }}>−{money(deductions?.penalty)}</span>} />
              <Row label="Loan Deduction" value={`−${money(deductions?.loan)}`} />
              <Row label="Advance Deduction" value={`−${money(deductions?.advance)}`} />
              <Divider sx={{ my: 0.5 }} />
              <Row bold label={<strong>Total Deductions</strong>} value={<strong>{money(deductions?.total)}</strong>} />
              <Divider sx={{ my: 0.5 }} />
              <Row bold label={<strong>Net Pay</strong>} value={<strong>{money(apiNet)}{netMismatch ? ` (calc ${money(calcNet)})` : ''}</strong>} />
            </Stack>
          </Box>
        </Stack>
      </Paper>

      {/* Advance Salary History (categorized) */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="h6">Advance Salary History</Typography>
          <Typography variant="body2" color="text.secondary">Total: {money(advanceHistory?.grand || 0)}</Typography>
        </Stack>

        {advanceHistory && advanceHistory.flat.length > 0 ? (
          <Stack gap={1}>
            {Object.entries(advanceHistory.groups).map(([cat, arr], i) => (
              <Accordion disableGutters key={cat + i} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" gap={1} alignItems="center" sx={{ width: '100%', justifyContent: 'space-between' }}>
                    <Typography fontWeight={700}>{cat}</Typography>
                    <Chip size="small" variant="outlined" label={money(advanceHistory.catTotals[cat])} />
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 130 }}>Date</TableCell>
                        <TableCell align="right" sx={{ width: 140 }}>Amount</TableCell>
                        <TableCell>Note</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {arr.map((r, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{r.date || '-'}</TableCell>
                          <TableCell align="right">{money(r.amount)}</TableCell>
                          <TableCell>{r.note || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">No advance history.</Typography>
        )}
      </Paper>

      {/* Daily Breakdown */}
      <Typography variant="h6" gutterBottom>Daily Breakdown</Typography>

      {/* md+ table */}
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <Paper variant="outlined">
          <TableContainer sx={{ maxHeight: 560 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Basic</TableCell>
                  <TableCell align="right">Tips</TableCell>
                  <TableCell align="right">Service</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell>Flags</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((d, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell>{d.date}</TableCell>
                    <TableCell align="right">{fmt(d.daily_basic)}</TableCell>
                    <TableCell align="right">{fmt(d.daily_tip)}</TableCell>
                    <TableCell align="right">{fmt(d.daily_svc)}</TableCell>
                    <TableCell align="right">{fmt(d.daily_total)}</TableCell>
                    <TableCell>{renderFlags(d, slip)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      {/* xs/sm card list */}
      <Stack sx={{ display: { xs: 'flex', md: 'none' } }} gap={1.25}>
        {rows.map((d, idx) => (
          <Paper key={idx} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
              <Typography fontWeight={700}>{d.date}</Typography>
              <Typography variant="caption" color="text.secondary">Total: {money(d.daily_total)}</Typography>
            </Stack>
            <Stack direction="row" flexWrap="wrap" columnGap={3} rowgap={0.5}>
              <Cell label="Basic" value={money(d.daily_basic)} />
              <Cell label="Tips" value={money(d.daily_tip)} />
              <Cell label="Service" value={money(d.daily_svc)} />
            </Stack>
            <Box sx={{ mt: 1 }}>{renderFlags(d, slip)}</Box>
          </Paper>
        ))}
      </Stack>

      <Snackbar
        open={snack.open}
        autoHideDuration={2500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

/* ---------- Small presentational helpers ---------- */
function Row({ label, value, bold }) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 0.25 }}>
      <Typography variant="body2" sx={{ opacity: .85 }}>{label}</Typography>
      <Typography variant="body2" fontWeight={bold ? 800 : 600}>{value}</Typography>
    </Stack>
  );
}

function Cell({ label, value }) {
  return (
    <Stack direction="row" gap={0.75} alignItems="center">
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{label}:</Typography>
      <Typography variant="body2" fontWeight={600}>{value}</Typography>
    </Stack>
  );
}

function renderFlags(d, slip) {
  const perDay = Number(slip?.basic_salary_per_day || 0);
  const flagHalf = typeof d.flag_half !== 'undefined' ? !!d.flag_half : perDay > 0 && approxEq(d.daily_basic, perDay / 2);
  const flagNoTips = typeof d.flag_no_tips !== 'undefined' ? !!d.flag_no_tips : Number(d.daily_tip) === 0;
  const flagNoSvc = typeof d.flag_no_svc !== 'undefined' ? !!d.flag_no_svc : Number(d.daily_svc) === 0;
  return (
    <Stack direction="row" spacing={1} flexWrap="wrap">
      {flagHalf && (
        <Chip size="small" icon={<AccessTimeIcon />} label="Half Day" variant="outlined" />
      )}
      {flagNoTips && (
        <Tooltip title={d.flag_no_tips === false ? 'Tips = 0 (pool zero)' : 'Tips excluded'}>
          <Chip size="small" color="warning" icon={<MoneyOffIcon />} label="No Tips" variant="outlined" />
        </Tooltip>
      )}
      {flagNoSvc && (
        <Tooltip title={d.flag_no_svc === false ? 'Service = 0 (pool zero)' : 'Service excluded'}>
          <Chip size="small" color="error" icon={<DoNotDisturbAltIcon />} label="No Service" variant="outlined" />
        </Tooltip>
      )}
      {!flagHalf && !flagNoTips && !flagNoSvc && (
        <Typography variant="caption" color="text.secondary">—</Typography>
      )}
    </Stack>
  );
}
